import { makeAutoObservable, runInAction } from 'mobx';
import {
  CUSTOMER_TYPE,
  USER_CUSTOMER_STATUSES,
  TWO_FACTOR_AUTH_TYPES,
  CUSTOMER_FEATURES
} from 'components/common/constants';
import { base64ToImageUrl } from 'components/utils';
import {
  setSelectedMultiAccount,
  getSelectedMultiAccount,
  setSecureUserPhone,
  setConfirmationActionType,
  setInternalUserId,
  setCustomerId,
  setCustomerType,
  setAvailableConfirmationTypes
} from 'services/authUtils';
import {
  getUserInformation,
  getAccounts,
  getApplicationState,
  getTariffs,
  getUserInfoByCustomerNumber,
  enableGoogle2FARequest,
  confirmGoogle2FARequest,
  beginVerificationRequest,
  getApplicationConstantsList,
  activateSms2FARequest,
  activateWhatsApp2FARequest,
  disableGoogle2FARequest,
  reactivateGoogle2FARequest,
  downloadAccountDetailsPdfRequest
} from 'services/requestAgent';
import { isCryptoProvider } from 'services/utils';

class UserStore {
  isLoading = false;
  isLoadingAccountDetails = false;
  isInitialized = false;
  isVerificationLoading = false;
  error = null;
  errorAccountDetails = null;
  userData = {};
  userAccounts = [];
  currentAccount = null;
  appFeatures = {};
  appConstants = {};
  appFilterStatuses = {};
  isCustomerVerified = null;
  isUserDataFetched = false;
  enabledTariffs = [];
  currentCustomerPermissions = null;
  isShowGoogle2FaPopUp = false;
  isGoogle2FaEnablingSuccess = false;
  isGoogle2FaDisablingConfirmation = false;
  isWhatsApp2FaDisablingConfirmation = false;
  isGoogle2FaDisablingSuccess = false;
  isWhatsApp2FaDisablingSuccess = false;
  isGoogle2FAActivated = false;
  isWhatsApp2FAActivated = false;
  isSms2FAActivated = false;
  isPushNotifications2FAActivated = false;
  is2FALoading = false;
  googleQrCode = null;
  confirmationId = null;
  verificationAccessToken = null;
  cryptoTermsAccepted = null;

  constructor() {
    makeAutoObservable(this);
  }

  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
  }

  setIsLoadingAccountDetails(status) {
    this.isLoadingAccountDetails = status;
    this.errorAccountDetails = null;
  }

  setIs2FALoading(status) {
    this.is2FALoading = status;
    this.error = null;
  }

  setIsVerificationLoading(status) {
    this.isVerificationLoading = status;
    this.error = null;
  }

  getVerifiedStatus(userData) {
    if (process.env.REACT_APP_KYC_MODE !== '/external') return true;

    if (userData.account?.type === CUSTOMER_TYPE.COMPANY || userData.account?.type === CUSTOMER_TYPE.INDIVIDUAL) {
      return userData.account?.status === USER_CUSTOMER_STATUSES.VERIFIED;
    }
  }

  setCurrentAccount(account) {
    this.currentAccount = account;
  }

  closeIsShowGoogle2FaPopUp() {
    this.isShowGoogle2FaPopUp = false;
    this.confirmationId = null;
    this.googleQrCode = null;
  }

  setIsGoogle2FaEnablingSuccess(status) {
    this.isGoogle2FaEnablingSuccess = status;
  }

  setIsGoogle2FaDisablingConfirmation(status) {
    this.isGoogle2FaDisablingConfirmation = status;
  }

  setIsWhatsApp2FaDisablingConfirmation(status) {
    this.isWhatsApp2FaDisablingConfirmation = status;
  }

  setIsGoogle2FaDisablingSuccess(status) {
    this.isGoogle2FaDisablingSuccess = status;
  }

  setIsWhatsApp2FaDisablingSuccess(status) {
    this.isWhatsApp2FaDisablingSuccess = status;
  }

  resetEnabledTariffs() {
    this.enabledTariffs = [];
  }

  updateRepresentativeData(updatedRepresentative, isRemove = false) {
    const existingRepresentative = this.userData.representatives?.find(({ id }) => id === updatedRepresentative.id);

    this.userData.representatives = existingRepresentative
      ? this.userData.representatives.reduce((result, representative) => {
          if (representative.id === updatedRepresentative.id) {
            if (isRemove) return result;
            result.push(updatedRepresentative);
            return result;
          }
          result.push(representative);

          return result;
        }, [])
      : [...this.userData.representatives, updatedRepresentative];
  }

  getCurrentCustomerPermissions(availableCustomers, customerNumber) {
    return (
      availableCustomers.find(
        (customer) =>
          customer.account_number === customerNumber || customer?.customer_account?.account_number === customerNumber
      )?.permissions || null
    );
  }

  hasPermissions = (expectedPermissions = []) => {
    const userPermissions = this.currentCustomerPermissions;
    // When we don't have permission in account object -> main account
    if (!userPermissions) {
      return true;
    }
    return !!userPermissions?.filter((permission) => expectedPermissions.includes(permission)).length;
  };

  changeCurrentUserAccount = async (customerNumber) => {
    runInAction(() => {
      this.verificationAccessToken = null;
      this.currentCustomerPermissions = this.getCurrentCustomerPermissions(
        this.userData.availableAccounts,
        customerNumber
      );
    });
    setSelectedMultiAccount(customerNumber);
    await this.loadUserData(customerNumber);
  };

  async getAppState() {
    try {
      this.setIsLoading(true);
      const state = await getApplicationState();
      const constants = await getApplicationConstantsList();
      runInAction(() => {
        this.appConstants = state?.constants;
        this.appFilterStatuses = constants?.filterStatuses;
        this.appFeatures = state?.features;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isLoading = false;
      });
      throw err;
    }
  }

  getCurrentAccount(selectedSharedAccountData, ownUserAccounts) {
    if (selectedSharedAccountData) {
      return selectedSharedAccountData;
    }
    return ownUserAccounts.find(({ type }) => type === CUSTOMER_TYPE.COMPANY) || ownUserAccounts[0];
  }

  async loadUserData(customerNumber, isAfterSuccessfulRegistration) {
    this.setIsLoading(true);
    try {
      this.setIsLoading(true);
      let selectedSharedAccountData = null;
      const data = await getUserInformation();
      if (data?.user_info?.phone) {
        setSecureUserPhone(data?.user_info?.phone);
      }
      const selectedCustomerNumber = isAfterSuccessfulRegistration
        ? data.user_accounts[data.user_accounts.length - 1]?.account_number
        : customerNumber || getSelectedMultiAccount() || null;
      if (selectedCustomerNumber) {
        selectedSharedAccountData = await getUserInfoByCustomerNumber(selectedCustomerNumber);
      }
      // DTO changed for UI representation (past representation without multiaccounts)
      const userDataRepresentation = {
        account: this.getCurrentAccount(selectedSharedAccountData, data.user_accounts),
        representatives: data.representatives,
        availableAccounts: [...data.user_accounts, ...data.shared_accounts],
        ...data.user_info
      };

      const currentCustomerPermissions = this.getCurrentCustomerPermissions(
        userDataRepresentation.availableAccounts,
        selectedCustomerNumber
      );

      let accounts = [];
      // Move accounts logic to separate store!
      if (!userDataRepresentation?.account?.account_number) {
        // return (this.error = { code: 'ACCOUNT_NOT_REGISTERED' });
        this.error = { code: 'ACCOUNT_NOT_REGISTERED' };
      } else {
        accounts = await getAccounts(userDataRepresentation.account.account_number);
      }

      runInAction(() => {
        setInternalUserId(userDataRepresentation?.user_id);
        setCustomerId(userDataRepresentation?.account?.account_number);
        setCustomerType(userDataRepresentation?.account?.type);
        setConfirmationActionType(userDataRepresentation.two_factor_auth_type);
        if (userDataRepresentation.available_confirmation_types) {
          setAvailableConfirmationTypes(userDataRepresentation.available_confirmation_types);
        }
        this.userData = userDataRepresentation;
        this.isGoogle2FAActivated = userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.isWhatsApp2FAActivated = userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.WHATSAPP;
        this.isSms2FAActivated = userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.SMS;
        this.isPushNotifications2FAActivated =
          userDataRepresentation?.two_factor_auth_type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.currentCustomerPermissions = currentCustomerPermissions;
        this.userAccounts = accounts;
        this.currentAccount = accounts.length > 0 ? accounts[0] : null;
        this.isCustomerVerified = this.getVerifiedStatus(userDataRepresentation);
        this.isUserDataFetched = true;
        this.isInitialized = true;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isLoading = false;
      });
      throw err;
    }
  }

  async loadEnabledTariffs(accountNumber, transactional) {
    try {
      // Move tariffs logic to separate store as well likely
      const data = await getTariffs(this.userData.account?.account_number, accountNumber, transactional);
      runInAction(() => {
        this.enabledTariffs = data;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    }
  }

  // Temporary solution for updating balances
  async getUserAccounts() {
    try {
      const accounts = await getAccounts(this.userData.account.account_number);
      runInAction(() => {
        this.userAccounts = accounts;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    }
  }

  async downloadAccountDetailsPdf(customerNumber, accountNumber) {
    try {
      this.setIsLoadingAccountDetails(true);
      await downloadAccountDetailsPdfRequest(customerNumber, accountNumber);
    } catch (err) {
      runInAction(() => {
        this.errorAccountDetails = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingAccountDetails = false;
      });
    }
  }

  isFeesEnabled() {
    return !!this.appFeatures.tariffsEnabled;
  }

  isCardsEnabled() {
    return !!this.appFeatures.cardsEnabled;
  }

  isRepresentativesEnabled() {
    return !!this.appFeatures.representativesEnabled;
  }

  isNewPaymentFormEnabled() {
    if (!this.userData?.account?.features) {
      return false;
    }
    const feature = this.userData.account.features.find((f) => f.feature === CUSTOMER_FEATURES.NEW_PAYMENT_FORM);
    return feature?.enabled === true;
  }

  getKycProvider() {
    return this.appFeatures.kycProvider;
  }

  getKybProvider() {
    return this.appFeatures.kybProvider;
  }

  async enableGoogle2FA() {
    this.setIs2FALoading(true);
    try {
      const { qr_code, confirmation_uid } = await enableGoogle2FARequest();

      runInAction(() => {
        this.googleQrCode = base64ToImageUrl(qr_code);
        this.confirmationId = confirmation_uid;
        this.isShowGoogle2FaPopUp = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async reactivateGoogle2FA() {
    this.setIs2FALoading(true);
    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await reactivateGoogle2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isWhatsApp2FAActivated = false;
        this.isGoogle2FAActivated = true;
        this.isPushNotifications2FAActivated = false;
        this.isSms2FAActivated = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async confirmGoogle2FAEnabling(code) {
    this.setIs2FALoading(true);
    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await confirmGoogle2FARequest({ confirmation_id: this.confirmationId, code });

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.confirmationId = null;
        this.googleQrCode = null;
        this.isGoogle2FaEnablingSuccess = true;
        this.isShowGoogle2FaPopUp = false;
        this.isGoogle2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.isWhatsApp2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.WHATSAPP;
        this.isPushNotifications2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.isSms2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.SMS;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async disableGoogle2FA() {
    this.setIs2FALoading(true);

    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await disableGoogle2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isGoogle2FaDisablingConfirmation = false;
        this.isGoogle2FaDisablingSuccess = true;
        this.isGoogle2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.isWhatsApp2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.WHATSAPP;
        this.isPushNotifications2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
        this.isSms2FAActivated = twoFactorAuthType === TWO_FACTOR_AUTH_TYPES.SMS;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async activateWhatsApp2FA() {
    this.setIs2FALoading(true);
    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await activateWhatsApp2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isGoogle2FAActivated = false;
        this.isPushNotifications2FAActivated = false;
        this.isSms2FAActivated = false;
        this.isWhatsApp2FAActivated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async activateSms2FA() {
    this.setIs2FALoading(true);

    try {
      const { two_factor_auth_type: twoFactorAuthType, available_confirmation_types: availableConfirmationTypes } =
        await activateSms2FARequest();

      runInAction(() => {
        setConfirmationActionType(twoFactorAuthType);
        setAvailableConfirmationTypes(availableConfirmationTypes);
        this.isWhatsApp2FAActivated = false;
        this.isGoogle2FAActivated = false;
        this.isPushNotifications2FAActivated = false;
        this.isSms2FAActivated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.is2FALoading = false;
      });
    }
  }

  async beginVerification(customerNumber, data) {
    this.setIsVerificationLoading(true);
    try {
      const { token: accessToken, amlStatus } = await beginVerificationRequest(customerNumber, data);

      runInAction(() => {
        this.verificationAccessToken = accessToken ? accessToken : null;

        if (this.userData?.account) {
          this.userData.account.amlLevelName = data?.level_name;
          this.userData.account.amlStatus = amlStatus ? amlStatus : null;
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isVerificationLoading = false;
      });
    }
  }

  hasCryptoAccounts() {
    return this.userAccounts?.some((a) => isCryptoProvider(a.transfer_provider));
  }
}

export default new UserStore();
